import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { MenuCategory } from '@/types'
import type { SelectGroup } from '@/components/forms/fields'

import Image from 'next/image'
import { useRouter } from 'next/router'
import { ChevronDown, CreditCard, MapPinned, UtensilsCrossed } from 'lucide-react'
import { getMenu } from '@/lib/db/queries'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'
import { DiasporaForm } from '@/components/forms/diaspora-form'
import { diasporaSteps } from '@/constants/process'
import { contact, whatsappLink } from '@/constants/site'

interface OrderFromAbroadProps {
    itemGroups: Array<SelectGroup>
}

const STEP_ICONS = [UtensilsCrossed, MapPinned, CreditCard]

const CURRENCY_CODES = ['NGN', 'USD', 'GBP']

const faqs = [
    {
        question: 'How do I pay from abroad?',
        answer: 'Choose Naira, US Dollars or British Pounds on the form. Once we confirm your order, we send you the payment options available for your chosen currency.'
    },
    {
        question: 'Where do you deliver?',
        answer: 'Our kitchen is in Akure, Ondo State, and we serve clients across Nigeria. Share the delivery address and we will confirm the delivery arrangements with you before you pay.'
    },
    {
        question: 'Can I include a personal message?',
        answer: 'Yes. Write your note in the form and we will include it with the delivery, so your loved one knows exactly who it is from.'
    },
    {
        question: 'When is my order confirmed?',
        answer: 'We review every request and contact you on WhatsApp or email to confirm the menu, the delivery date and your total before anything is prepared.'
    }
]

const OrderFromAbroad: FC<OrderFromAbroadProps> = ({ itemGroups = [] }): ReactNode => {
    const router = useRouter()
    const requestedItem = typeof router.query.item === 'string' ? router.query.item : ''
    const requestedCurrency = typeof router.query.currency === 'string' ? router.query.currency.toUpperCase() : ''

    const knownItem = itemGroups.some(group => group.options.some(option => (typeof option === 'string' ? option : option.value) === requestedItem))
    const initialItem = knownItem ? requestedItem : ''
    const initialCurrency = CURRENCY_CODES.includes(requestedCurrency) ? requestedCurrency : ''

    return (
        <Layout>
            <Seo
                title='Order from abroad'
                description='Living in the UK, USA, Canada or anywhere abroad? Order Nigerian meals, surprise food trays and special packages for your loved ones in Nigeria and pay in NGN, USD or GBP.'
                ogImage='https://loveth-tasty-global.cdn.modulify.website/gallery/surprise-tray.jpg'
            />

            <PageHero
                eyebrow='Order From Abroad'
                breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Order From Abroad' }]}
                title={(
                    <>
                        Send the taste of home{' '}
                        <em className='text-brand-burnt'>to the people you love.</em>
                    </>
                )}
                description={(
                    <p>
                        Whether you&apos;re in the UK, USA, Canada, or anywhere around the world, you can arrange delicious Nigerian meals, food trays, and special catering packages for your family and friends back home.
                    </p>
                )}
                image={{
                    src: '/images/diaspora/surprise-tray.jpg',
                    alt: 'A surprise food tray with jollof rice, grilled chicken, small chops, zobo and a greeting card'
                }}
            />

            <section aria-labelledby='abroad-steps-title' className='bg-brand-charcoal py-14 text-brand-white'>
                <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-10'>
                    <h2 id='abroad-steps-title' className='sr-only'>
                        How ordering from abroad works
                    </h2>

                    <ol className='grid gap-8 md:grid-cols-3'>
                        {diasporaSteps.map((step, index) => {
                            const Icon = STEP_ICONS[index] ?? UtensilsCrossed

                            return (
                                <li key={step.title} className='flex gap-5'>
                                    <span className='inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <div>
                                        <p className='font-heading text-sm text-brand-gold'>
                                            Step {index + 1}
                                        </p>

                                        <h3 className='mt-1 font-semibold'>
                                            {step.title}
                                        </h3>

                                        <p className='mt-1.5 text-sm leading-relaxed text-brand-mist'>
                                            {step.text}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </section>

            <section aria-label='Order form' className='bg-brand-white py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12 lg:px-10'>
                    <div className='lg:col-span-8'>
                        <div className='rounded-[2rem] border border-brand-sand bg-brand-white p-6 shadow-[0_50px_100px_-60px_rgba(32,32,32,0.5)] sm:p-10'>
                            <DiasporaForm
                                key={`${initialItem}-${initialCurrency}`}
                                itemGroups={itemGroups}
                                initialItem={initialItem}
                                initialCurrency={initialCurrency}
                            />
                        </div>
                    </div>

                    <aside className='space-y-5 lg:col-span-4'>
                        <div className='relative overflow-hidden rounded-[1.75rem] bg-brand-charcoal text-brand-white'>
                            <div className='relative aspect-[16/10]'>
                                <Image
                                    src='/images/diaspora/video-call.jpg'
                                    alt='A smiling woman abroad holding her phone, arranging a meal for her family'
                                    fill
                                    sizes='(min-width: 1024px) 30vw, 100vw'
                                    className='object-cover object-[50%_70%]'
                                />
                            </div>

                            <div className='p-7'>
                                <h2 className='font-heading text-2xl font-semibold'>
                                    Rather talk it through?
                                </h2>

                                <p className='mt-2 text-sm leading-relaxed text-brand-mist'>
                                    Message us on WhatsApp from any country. Call us on {contact.phoneInternational}.
                                </p>

                                <a
                                    href={whatsappLink("Hello Loveth Tasty, I live abroad and I'd like to send food to my family in Nigeria.")}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-brand-charcoal transition-transform duration-300 hover:-translate-y-0.5'
                                >
                                    <WhatsappIcon className='size-4' />
                                    Chat on WhatsApp
                                </a>
                            </div>
                        </div>

                        <div className='rounded-[1.75rem] border border-brand-sand bg-brand-cream p-7'>
                            <h2 className='font-heading text-2xl font-semibold text-brand-charcoal'>
                                Questions, answered
                            </h2>

                            <div className='mt-4 divide-y divide-brand-sand'>
                                {faqs.map(faq => (
                                    <details key={faq.question} className='group py-4'>
                                        <summary className='flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-brand-charcoal [&::-webkit-details-marker]:hidden'>
                                            {faq.question}
                                            <ChevronDown className='size-4 shrink-0 text-brand-burnt transition-transform duration-300 group-open:rotate-180' aria-hidden='true' />
                                        </summary>

                                        <p className='mt-3 text-sm leading-relaxed text-brand-stone'>
                                            {faq.answer}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </Layout>
    )
}

const PRIORITY_CATEGORIES = ['food-trays', 'special-packages']

const toGroups = (menu: Array<MenuCategory>): Array<SelectGroup> => {
    return [...menu]
        .sort((first, second) => {
            const firstRank = PRIORITY_CATEGORIES.indexOf(first.slug)
            const secondRank = PRIORITY_CATEGORIES.indexOf(second.slug)

            return (firstRank === -1 ? 99 : firstRank) - (secondRank === -1 ? 99 : secondRank)
        })
        .map(category => ({
            label: category.name,
            options: category.items.map(item => item.name)
        }))
}

export const getStaticProps: GetStaticProps<OrderFromAbroadProps> = async () => {
    try {
        return { props: { itemGroups: toGroups(await getMenu()) }, revalidate: 60 }
    }
    catch (error) {
        console.error('Order from abroad page could not load the menu:', error instanceof Error ? error.message : error)

        return { props: { itemGroups: [] }, revalidate: 10 }
    }
}

export default OrderFromAbroad
